import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { UserBuyInLog } from "../../types";
import { collection, getDocs, onSnapshot, query } from "firebase/firestore";
import { db } from "../../firebase";
import { AppDispatch } from "../store";

interface UserBuyInLogState {
  records: UserBuyInLog[];
}

const initialState: UserBuyInLogState = {
  records: [],
};

// Async Thunk to Fetch userBuyInLogs  from Firestore
export const fetchUserBuyInLogs = createAsyncThunk(
  "userBuyInLogSlice/fetchUserBuyInLogs",
  async (_, { rejectWithValue }) => {
    try {
      const userBuyInLogsRef = collection(db, "userBuyInLogs");
      const q = query(userBuyInLogsRef);
      const querySnapshot = await getDocs(q);
      const players: UserBuyInLog[] = [];
      querySnapshot.forEach((doc) => {
        players.push({ id: doc.id, ...doc.data() } as UserBuyInLog);
      });

      return players;
    } catch (error) {
      return rejectWithValue("Failed to fetch players in group");
    }
  }
);

export const subscribeToUserBuyInLogs = () => (dispatch: AppDispatch) => {
  const userBuyInLogsRef = collection(db, "userBuyInLogs");
  const q = query(userBuyInLogsRef);

  // Subscribe to real-time changes
  const unsubscribe = onSnapshot(q, (snapshot) => {
    snapshot.docChanges().forEach((change) => {
      const record = {
        id: change.doc.id,
        ...change.doc.data(),
      } as UserBuyInLog;

      if (change.type === "added") {
        dispatch(addRecord(record));
      } else if (change.type === "modified") {
        dispatch(updateRecord(record));
      } else if (change.type === "removed") {
        if (record.id) {
          dispatch(removeRecord(record.id));
        }
      }
    });
  });

  return unsubscribe; // Return the unsubscribe function
};

const userBuyInLogSlice = createSlice({
  name: "userBuyInLogs",
  initialState,
  selectors: {
    getSumBuyInPrice: (state) => {
      return state.records.reduce((acc, curr) => acc + curr.buyInPrice, 0);
    },
    getMappingCountBuyInByPlayerId: (state) => {
      return state.records.reduce((acc, curr) => {
        if (!acc[curr.playerId]) {
          acc[curr.playerId] = 0;
        }
        acc[curr.playerId] += 1;
        return acc;
      }, {} as Record<string, number>);
    },
  },
  reducers: {
    clearState: (state) => {
      state.records = [];
    },
    addRecord(state, action: PayloadAction<UserBuyInLog>) {
      state.records.push(action.payload);
    },
    updateRecord(state, action: PayloadAction<UserBuyInLog>) {
      const index = state.records.findIndex((p) => p.id === action.payload.id);
      if (index !== -1) {
        state.records[index] = action.payload;
      }
    },
    removeRecord(state, action: PayloadAction<string>) {
      state.records = state.records.filter((p) => p.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserBuyInLogs.fulfilled, (state, action) => {
      state.records = action.payload;
    });
  },
});

export const { clearState, addRecord, updateRecord, removeRecord } =
  userBuyInLogSlice.actions;
export const { getSumBuyInPrice, getMappingCountBuyInByPlayerId } =
  userBuyInLogSlice.selectors;

export default userBuyInLogSlice.reducer;
